import { useCallback, useMemo, useState } from "react"

import { App } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import Creators from "./reducer"

const CLOSED = { open: false, kind: null, record: null }

/**
 * All the editing state the Payment Matching page owns, kept out of the page body.
 *
 * The page owns it rather than each row, because the rules are about rows *together*: only one row
 * can be in edit mode, opening a row's Action menu closes anything else that row has open, and every
 * icon action reaches a whole group of rows that the clicked row cannot see.
 *
 * Transient dialog state stays in component state on purpose. Redux holds server data; a half-typed
 * name and a half-answered dialog are not that, and persisting them would restore a stale dialog.
 */
export const usePaymentMatchingEditing = ({ onChanged }) => {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const dispatch = useDispatch()

  const [editingId, setEditingId] = useState(null)
  const [confirm, setConfirm] = useState(CLOSED)
  const [conflict, setConflict] = useState(null)
  const [conflictChoice, setConflictChoice] = useState(null)
  const [iconPopover, setIconPopover] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [pendingName, setPendingName] = useState("")
  // The id of the row whose group is being looked up, so its Save button can show it is working.
  const [groupPending, setGroupPending] = useState(null)
  // null = closed, { record } = scoped to that row, {} = the whole page. Opening the log must not
  // disturb the table's filters, sort or page, so it lives beside them rather than in them.
  const [activityLog, setActivityLog] = useState(null)

  // One operation, one toast, naming the value it refers to — with several hundred rows and one row
  // edited at a time, a message without the value is not verifiable. Toasts carry no undo: the
  // activity log is the record of what changed.
  const toast = useCallback(
    ({ message, description }) =>
      notification.success({ message, description, placement: "bottomRight" }),
    [notification]
  )

  const closeEverything = useCallback(() => {
    setEditingId(null)
    setConfirm(CLOSED)
    setConflict(null)
    setConflictChoice(null)
    setIconPopover(null)
    setUploadError(null)
    setGroupPending(null)
  }, [])

  const afterChange = useCallback(
    (...toasts) => {
      closeEverything()
      toasts.filter(Boolean).forEach(toast)
      onChanged()
    },
    [closeEverything, toast, onChanged]
  )

  /* ---------------- UI name ---------------- */

  const onStartEdit = useCallback((record) => {
    // Opening another row discards the first one's text without a prompt, as specified.
    setConfirm(CLOSED)
    setIconPopover(null)
    setEditingId(record.id)
  }, [])

  const onCancelEdit = useCallback(() => setEditingId(null), [])

  const onSaveName = useCallback(
    (record, uiName) => {
      setPendingName(uiName)

      // Clearing a name takes the row out of its group, so the result is the row on its own: no
      // partners to list and no icon to reconcile. Asking the server would be wrong anyway, since an
      // empty candidate name is indistinguishable from "no candidate given".
      if (!uiName) {
        setConfirm({ open: true, kind: "rename", record, affectedRawValues: [record.current_name] })
        return
      }

      // Nothing is shown until the resulting group is known: the conflict dialog *replaces* the
      // rename confirmation, so flashing the confirmation first would put support through both.
      setGroupPending(record.id)
      dispatch(
        Creators.getPaymentMatchingGroupRequest({
          id: record.id,
          ui_name: uiName,
          onSuccess: (group) => {
            setGroupPending(null)

            if (group.requires_icon_choice) {
              setConflictChoice(null)
              setConflict({ record, ...group })
              return
            }

            setConfirm({ open: true, kind: "rename", record, affectedRawValues: group.raw_values })
          },
          onError: (error) => {
            setGroupPending(null)
            notification.error({ message: error?.detail || t("Server response error") })
          }
        })
      )
    },
    [dispatch, notification, t]
  )

  const submitRename = useCallback(
    (record, uiName, iconChoice) => {
      dispatch(
        Creators.renamePaymentMatchingRequest({
          id: record.id,
          ui_name: uiName,
          icon_choice: iconChoice,
          onSuccess: (response) =>
            afterChange(
              {
                message: uiName ? t("Display name saved") : t("Display name removed"),
                description: record.current_name
              },
              // A conflict resolution is two operations, so the icon rewrite earns its own message —
              // one for the whole group, not one per row it touched.
              response?.icons_changed_raw_values?.length
                ? {
                    message: t("Icon updated"),
                    description: response.icons_changed_raw_values.join(", ")
                  }
                : null
            ),
          onError: (error) => {
            // The rename lost a race with another edit and now conflicts: swap the confirmation for
            // the dialog rather than failing silently.
            if (error?.code === "icon_conflict") {
              setConfirm(CLOSED)
              setConflictChoice(null)
              setConflict({ record, ...error })
              return
            }

            notification.error({ message: error?.detail || t("Server response error") })
          }
        })
      )
    },
    [dispatch, afterChange, notification, t]
  )

  /* ---------------- Icons ---------------- */

  const onViewHistory = useCallback((record) => setActivityLog({ record }), [])

  const onOpenRowMenu = useCallback(() => {
    // Opening the menu closes any open icon popover and any row in edit mode.
    setEditingId(null)
    setIconPopover(null)
    setUploadError(null)
  }, [])

  const onAddIcon = useCallback(
    (record) => {
      setUploadError(null)
      setIconPopover(record)
      dispatch(Creators.getPaymentMatchingIconsRequest())
    },
    [dispatch]
  )

  const applyIcon = useCallback(
    (record, icon) => {
      dispatch(
        Creators.setPaymentMatchingIconRequest({
          id: record.id,
          icon_id: icon.id,
          onSuccess: () =>
            afterChange({
              message: record.icon_url ? t("Icon replaced") : t("Icon set"),
              description: record.current_name
            }),
          onError: (error) =>
            notification.error({ message: error?.detail || t("Server response error") })
        })
      )
    },
    [dispatch, afterChange, notification, t]
  )

  const onPickLibraryIcon = useCallback(
    (record, icon) => {
      // Replacing an existing icon is confirmed first; a first assignment is not a replacement.
      if (record.icon_url) {
        setIconPopover(null)
        setConfirm({ open: true, kind: "replaceIcon", record, icon, affectedRawValues: [] })
        dispatch(
          Creators.getPaymentMatchingGroupRequest({
            id: record.id,
            onSuccess: (group) =>
              setConfirm({
                open: true,
                kind: "replaceIcon",
                record,
                icon,
                affectedRawValues: group.raw_values
              })
          })
        )
        return
      }

      applyIcon(record, icon)
    },
    [dispatch, applyIcon]
  )

  const submitUpload = useCallback(
    (record, file) => {
      const formData = new FormData()
      formData.append("file", file)

      setUploadError(null)
      dispatch(
        Creators.uploadPaymentMatchingIconRequest({
          id: record.id,
          formData,
          onSuccess: () =>
            afterChange({
              message: record.icon_url ? t("Icon replaced") : t("Icon set"),
              description: record.current_name
            }),
          // A validation failure is shown inside the popover, which reopens with the file's context
          // and names the rule that failed. Never a toast.
          onError: (error) => {
            setConfirm(CLOSED)
            setIconPopover(record)
            setUploadError(
              error?.file?.[0] || error?.detail || t("The file could not be uploaded.")
            )
          }
        })
      )
    },
    [dispatch, afterChange, t]
  )

  const onUploadIcon = useCallback(
    (record, file) => {
      // Replacing is confirmed first, and identically whether the new icon comes from an upload or
      // from the library. A first assignment is not a replacement, so it applies straight away.
      if (record.icon_url) {
        setIconPopover(null)
        setConfirm({ open: true, kind: "replaceIcon", record, file, affectedRawValues: [] })
        dispatch(
          Creators.getPaymentMatchingGroupRequest({
            id: record.id,
            onSuccess: (group) =>
              setConfirm({
                open: true,
                kind: "replaceIcon",
                record,
                file,
                affectedRawValues: group.raw_values
              })
          })
        )
        return
      }

      submitUpload(record, file)
    },
    [dispatch, submitUpload]
  )

  const onDeleteIcon = useCallback(
    (record) => {
      setConfirm({ open: true, kind: "deleteIcon", record, affectedRawValues: [] })
      dispatch(
        Creators.getPaymentMatchingGroupRequest({
          id: record.id,
          onSuccess: (group) =>
            setConfirm({
              open: true,
              kind: "deleteIcon",
              record,
              affectedRawValues: group.raw_values
            })
        })
      )
    },
    [dispatch]
  )

  const submitDeleteIcon = useCallback(
    (record) => {
      dispatch(
        Creators.deletePaymentMatchingIconRequest({
          id: record.id,
          onSuccess: () =>
            afterChange({ message: t("Icon removed"), description: record.current_name }),
          onError: (error) =>
            notification.error({ message: error?.detail || t("Server response error") })
        })
      )
    },
    [dispatch, afterChange, notification, t]
  )

  /* ---------------- Dialog wiring ---------------- */

  const onConfirm = useCallback(() => {
    if (confirm.kind === "rename") return submitRename(confirm.record, pendingName)
    // One confirmation, two sources: a pending file means an upload, otherwise a library pick.
    if (confirm.kind === "replaceIcon") {
      return confirm.file
        ? submitUpload(confirm.record, confirm.file)
        : applyIcon(confirm.record, confirm.icon)
    }
    if (confirm.kind === "deleteIcon") return submitDeleteIcon(confirm.record)
  }, [confirm, pendingName, submitRename, applyIcon, submitUpload, submitDeleteIcon])

  // Dismissing a rename confirmation returns to edit mode with the typed text intact.
  const onCancelConfirm = useCallback(() => setConfirm(CLOSED), [])

  const onSaveConflict = useCallback(
    () => submitRename(conflict.record, pendingName, conflictChoice),
    [conflict, pendingName, conflictChoice, submitRename]
  )

  // Cancelling the conflict dialog changes nothing at all and leaves the row in edit mode.
  const onCancelConflict = useCallback(() => {
    setConflict(null)
    setConflictChoice(null)
  }, [])

  const editing = useMemo(
    () => ({
      editingId,
      groupPending,
      onStartEdit,
      onCancelEdit,
      onSaveName,
      onViewHistory,
      onOpenRowMenu,
      onAddIcon,
      onDeleteIcon
    }),
    [
      editingId,
      groupPending,
      onStartEdit,
      onCancelEdit,
      onSaveName,
      onViewHistory,
      onOpenRowMenu,
      onAddIcon,
      onDeleteIcon
    ]
  )

  return {
    editing,
    activityLog,
    openPageLog: () => setActivityLog({}),
    closeActivityLog: () => setActivityLog(null),
    confirm,
    pendingName,
    onConfirm,
    onCancelConfirm,
    conflict,
    conflictChoice,
    setConflictChoice,
    onSaveConflict,
    onCancelConflict,
    iconPopover,
    uploadError,
    onPickLibraryIcon,
    onUploadIcon,
    closeIconPopover: () => {
      setIconPopover(null)
      setUploadError(null)
    }
  }
}
